import * as Phaser from 'phaser';
import { TITLE_SCENE_KEY } from './sceneKeys';

export const SCENE_SELECTOR_SCENE_KEY = 'SceneSelectorScene' as const;

const FONT = "'Almendra', serif";
const COLOR_INK = '#eae0c8';
const COLOR_INK_HOVER = '#ffffff';
const COLOR_BG = '#1a1a2e';
const COLOR_HEADER = '#f4d078';
const COLOR_HEADER_HOVER = '#ffe8a0';
const BG_ALPHA = 0.94;
const ROW_H = 24;
const PADDING = 20;
const SECTION_GAP = 12;
const MAP_HEIGHT = 160;

interface AreaDef {
  id: string;
  label: string;
  color: string;
  prefixes: string[];
  mapX: number;
  mapY: number;
}

const AREAS: AreaDef[] = [
  {
    id: 'hill',
    label: 'The Hill',
    color: '#8bc34a',
    prefixes: ['CottageCrest', 'TheCrossing', 'TalkingStump', 'FarPillar', 'CottageInterior', 'TheMeadow', 'TheSlope', 'TheLowWall'],
    mapX: 0.5,
    mapY: 0.5,
  },
  {
    id: 'verdana',
    label: 'Verdana Fields',
    color: '#66bb6a',
    prefixes: ['Donchitos', 'SaplingField', 'WeepingStandingStone', 'OldGroveShrine', 'SageRoad', 'HamletInterior', 'RiversideWander', 'EmptyField', 'RidgeAboveDonchitos', 'FallenLog'],
    mapX: 0.25,
    mapY: 0.25,
  },
  {
    id: 'sablewind',
    label: 'Sablewind Reaches',
    color: '#ffb74d',
    prefixes: ['Caravanserai', 'GrownOverOasis', 'KiteHillShrine', 'WindChimeHollow', 'CaravanRoad', 'DuneCrestWander', 'DryCanyon', 'SandFlat', 'AbandonedLoom', 'OpenDesert'],
    mapX: 0.25,
    mapY: 0.75,
  },
  {
    id: 'frostmere',
    label: 'Frostmere Expanse',
    color: '#81d4fa',
    prefixes: ['FrostBridge', 'HearthmereHold', 'WakeNotYetCairn', 'FrozenFalls', 'NwPeaks', 'ThawLine', 'HearthHallInterior', 'IceCaveWander', 'SnowField', 'FrozenLake', 'Treeline'],
    mapX: 0.75,
    mapY: 0.25,
  },
  {
    id: 'ember',
    label: 'Ember Mire',
    color: '#ef5350',
    prefixes: ['Ashvein', 'JuggernautsHearth', 'EmberShrine', 'CoolingFlow', 'AshPass', 'SeedlingClearing', 'AshveinForgeInterior', 'LavaTube', 'CoolingCrust', 'HollowLogBridge', 'CharGarden'],
    mapX: 0.75,
    mapY: 0.75,
  },
  {
    id: 'shared',
    label: 'Shared / Neutral',
    color: '#ce93d8',
    prefixes: ['Hearth', 'PathHome', 'ForestHollow', 'LakesideStones', 'HilltopVista'],
    mapX: 0.5,
    mapY: 0.15,
  },
];

const SYSTEM_AREA: AreaDef = {
  id: 'system',
  label: 'System / UI',
  color: '#90a4ae',
  prefixes: [],
  mapX: 0.5,
  mapY: 0.9,
};

const SKIP_SCENES: Set<string> = new Set([SCENE_SELECTOR_SCENE_KEY]);

function classifyScene(key: string): string {
  for (const area of AREAS) {
    if (area.prefixes.some((p) => key.startsWith(p))) {
      return area.id;
    }
  }
  return 'system';
}

export class SceneSelectorScene extends Phaser.Scene {
  public static readonly KEY = SCENE_SELECTOR_SCENE_KEY;
  private scrollContainer!: Phaser.GameObjects.Container;
  private scrollY = 0;
  private maxScroll = 0;
  private sectionYPositions: Map<string, number> = new Map();

  constructor() {
    super({ key: SCENE_SELECTOR_SCENE_KEY });
  }

  create(): void {
    const w = this.scale.width;
    const h = this.scale.height;

    this.add.rectangle(w / 2, h / 2, w, h, Phaser.Display.Color.HexStringToColor(COLOR_BG).color, BG_ALPHA)
      .setInteractive();

    this.add.text(w / 2, 10, 'Dev Scene Navigator', {
      fontFamily: FONT,
      fontSize: '20px',
      color: COLOR_INK,
    }).setOrigin(0.5, 0);

    this.createMiniMap(w);

    const allScenes = this.game.scene.scenes
      .map((s) => s.sys.settings.key)
      .filter((key): key is string => typeof key === 'string' && !SKIP_SCENES.has(key));

    const grouped = new Map<string, string[]>();
    for (const area of [...AREAS, SYSTEM_AREA]) {
      grouped.set(area.id, []);
    }
    for (const key of allScenes) {
      const areaId = classifyScene(key);
      grouped.get(areaId)!.push(key);
    }

    const contentStartY = MAP_HEIGHT + 44;
    this.scrollContainer = this.add.container(0, contentStartY);

    let curY = 0;
    const allAreas = [...AREAS, SYSTEM_AREA];

    for (const area of allAreas) {
      const scenes = grouped.get(area.id)!;
      if (scenes.length === 0) continue;

      this.sectionYPositions.set(area.id, curY);

      const header = this.add.text(PADDING, curY, `▸ ${area.label} (${scenes.length})`, {
        fontFamily: FONT,
        fontSize: '15px',
        color: area.color,
        fontStyle: 'bold',
      }).setOrigin(0, 0);
      this.scrollContainer.add(header);
      curY += 28;

      const colW = 260;
      const cols = Math.max(1, Math.floor((w - PADDING * 2) / colW));
      const maxPerCol = Math.ceil(scenes.length / cols);

      scenes.forEach((key, i) => {
        const col = Math.floor(i / maxPerCol);
        const row = i % maxPerCol;
        const x = PADDING + 12 + col * colW;
        const y = curY + row * ROW_H;

        const displayName = key.replace(/Scene$/, '');
        const label = this.add.text(x, y, displayName, {
          fontFamily: FONT,
          fontSize: '12px',
          color: COLOR_INK,
        }).setOrigin(0, 0).setInteractive({ useHandCursor: true });

        label.on('pointerover', () => label.setColor(COLOR_INK_HOVER));
        label.on('pointerout', () => label.setColor(COLOR_INK));
        label.on('pointerdown', () => {
          this.stopAllScenes(key);
          this.scene.start(key);
        });

        this.scrollContainer.add(label);
      });

      curY += maxPerCol * ROW_H + SECTION_GAP;
    }

    const visibleHeight = h - contentStartY - 40;
    this.maxScroll = Math.max(0, curY - visibleHeight);

    const maskShape = this.make.graphics({});
    maskShape.fillRect(0, contentStartY, w, visibleHeight);
    const mask = maskShape.createGeometryMask();
    this.scrollContainer.setMask(mask);

    this.input.on('wheel', (_pointer: Phaser.Input.Pointer, _gx: number[], _gy: number, _gz: number, dy: number) => {
      this.scrollY = Phaser.Math.Clamp(this.scrollY + dy * 0.5, 0, this.maxScroll);
      this.scrollContainer.y = contentStartY - this.scrollY;
    });

    const backLabel = this.add.text(w / 2, h - 14, 'Back to Title  [ESC]', {
      fontFamily: FONT,
      fontSize: '13px',
      color: COLOR_INK,
    }).setOrigin(0.5, 1).setInteractive({ useHandCursor: true });

    backLabel.on('pointerover', () => backLabel.setColor(COLOR_INK_HOVER));
    backLabel.on('pointerout', () => backLabel.setColor(COLOR_INK));
    backLabel.on('pointerdown', () => this.scene.start(TITLE_SCENE_KEY));

    const escKey = this.input.keyboard?.addKey(Phaser.Input.Keyboard.KeyCodes.ESC);
    escKey?.on('down', () => this.scene.start(TITLE_SCENE_KEY));
  }

  private createMiniMap(screenW: number): void {
    const mapW = Math.min(500, screenW - 80);
    const mapH = MAP_HEIGHT - 20;
    const mapX = (screenW - mapW) / 2;
    const mapY = 36;

    const g = this.add.graphics();
    g.lineStyle(1, 0x555555, 0.6);
    g.strokeRoundedRect(mapX, mapY, mapW, mapH, 8);

    const allAreas = [...AREAS, SYSTEM_AREA];
    for (const area of allAreas) {
      const cx = mapX + area.mapX * mapW;
      const cy = mapY + area.mapY * mapH;
      const color = Phaser.Display.Color.HexStringToColor(area.color).color;

      g.fillStyle(color, 0.25);
      g.fillCircle(cx, cy, 28);
      g.lineStyle(1.5, color, 0.8);
      g.strokeCircle(cx, cy, 28);

      const label = this.add.text(cx, cy, area.label.split(' ')[0] === 'The' ? 'Hill' : area.label.split(' ')[0], {
        fontFamily: FONT,
        fontSize: '11px',
        color: area.color,
        fontStyle: 'bold',
      }).setOrigin(0.5, 0.5);

      const hitZone = this.add.zone(cx, cy, 56, 56).setInteractive({ useHandCursor: true });

      hitZone.on('pointerover', () => {
        label.setColor(COLOR_HEADER_HOVER);
        label.setScale(1.1);
      });
      hitZone.on('pointerout', () => {
        label.setColor(area.color);
        label.setScale(1.0);
      });
      hitZone.on('pointerdown', () => {
        this.scrollToArea(area.id);
      });
    }

    // Draw roads from Hill center to each realm
    const hillCx = mapX + 0.5 * mapW;
    const hillCy = mapY + 0.5 * mapH;
    g.lineStyle(1, 0x666666, 0.4);
    for (const area of AREAS) {
      if (area.id === 'hill' || area.id === 'shared') continue;
      const ax = mapX + area.mapX * mapW;
      const ay = mapY + area.mapY * mapH;
      g.lineBetween(hillCx, hillCy, ax, ay);
    }
  }

  private scrollToArea(areaId: string): void {
    const targetY = this.sectionYPositions.get(areaId);
    if (targetY === undefined) return;
    const contentStartY = MAP_HEIGHT + 44;
    this.scrollY = Phaser.Math.Clamp(targetY, 0, this.maxScroll);
    this.scrollContainer.y = contentStartY - this.scrollY;
  }

  private stopAllScenes(except: string): void {
    for (const s of this.game.scene.scenes) {
      const k = s.sys.settings.key;
      if (k && k !== except && this.scene.isActive(k)) {
        this.scene.stop(k);
      }
    }
  }
}
